import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BlockIcon from '@mui/icons-material/Block';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import IntervalTimer from 'react-interval-timer';

import { useNotif } from 'contexts/NotifContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import { isValidUrl } from 'utils/functions';
import moment from 'utils/moment';
import { CUSTOMERS, DOMAINCONFIG, PML } from 'utils/rest';

import { Table, Text } from 'components/DataDisplay';
import { LinearProgress, Modal } from 'components/Feedback';
import { Button, Select, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import { Accordion } from 'components/Surfaces';

import BillingQuote, { formatEuros } from './BillingQuote';
import PreviewGallery from './PreviewGallery';

const IMPORT_TYPE = 'repiquage';
const AWAITING_VALIDATION = 'awaitingValidation';
const CANCELLING = 'cancelling';
const CANCELLED = 'cancelled';
const CANCELLATION_STALE_AFTER_MS = 5 * 60 * 1000;
const BILLING_ERROR_MESSAGE = "L'action n'a pas abouti, réessayez.";

const formatDuration = milliseconds => {
  const totalSeconds = Math.round(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return minutes ? `${minutes} min ${seconds} s` : `${seconds} s`;
};

const formatAverage = (milliseconds, count) => {
  const average = milliseconds / count;

  return average >= 60000
    ? formatDuration(average)
    : `${(average / 1000).toFixed(1)} s`;
};

const buildAveragesTxt = (milliseconds, units) => {
  const parts = [];

  units.forEach(unit => {
    if (unit.count > 0) {
      const average = formatAverage(milliseconds, unit.count);

      parts.push(`${average} ${unit.label} (${unit.count})`);
    }
  });

  return parts.join(' · ');
};

const importTimestamp = task => new Date(task?.createdAt || 0).getTime();

const campaignKeyOf = campaign => String(campaign?.key ?? '').trim();

const byMostRecentImport = (taskA, taskB) =>
  importTimestamp(taskB) - importTimestamp(taskA);

const campaignRowsOfTask = task => {
  const campaigns = task.campaigns?.length
    ? task.campaigns
    : [{ key: task.campaignKey }];

  return campaigns.map(campaign => ({ campaign, task }));
};

const lastRepiquageByCampaign = (tasks = []) => {
  const tasksByRecency = [...tasks].sort(byMostRecentImport);
  const seenCampaignKeys = new Set();
  const rows = [];

  tasksByRecency.forEach(task => {
    campaignRowsOfTask(task).forEach(row => {
      const campaignKey = campaignKeyOf(row.campaign);

      if (!seenCampaignKeys.has(campaignKey)) {
        seenCampaignKeys.add(campaignKey);
        rows.push(row);
      }
    });
  });

  return rows;
};

const readBillingState = setupTask => {
  const isInProgress = setupTask?.status === 'inProgress';
  const billing = setupTask?.billing || {};
  const upsertBilling = billing.upsert || {};
  const billingRunning =
    billing.status === 'inProgress' || upsertBilling.status === 'inProgress';
  const cancelling = billing.status === CANCELLING;
  const cancellationStalled =
    cancelling &&
    Date.now() - new Date(setupTask.updatedAt).getTime() >
      CANCELLATION_STALE_AFTER_MS;
  const cancelled = billing.status === CANCELLED;
  const { quote } = billing;

  return {
    isInProgress,
    billing,
    upsertBilling,
    billingRunning,
    awaitingValidation: billing.status === AWAITING_VALIDATION,
    billed: billing.status === 'done' && !isInProgress && !billingRunning,
    cancelling,
    cancellationStalled,
    cancelled,
    quote,
    showQuote: !!quote && !cancelling && !cancelled
  };
};

const RepiquageImport = ({ renderImportTypeSelect, onUnauthorized }) => {
  const { adminRights } = useUser();
  const { themeColors } = useTheme();
  const { notify } = useNotif();
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [computing, setComputing] = useState(false);
  const [urlError, setUrlError] = useState(false);
  const [setupTask, setSetupTask] = useState({});
  const [tasks, setTasks] = useState([]);
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [cancelAsked, setCancelAsked] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [confirmAsked, setConfirmAsked] = useState(false);
  const [validating, setValidating] = useState(false);
  const billingWatched = useRef(false);

  const {
    isInProgress,
    billing,
    upsertBilling,
    billingRunning,
    awaitingValidation,
    billed,
    cancelling,
    cancellationStalled,
    cancelled,
    quote,
    showQuote
  } = readBillingState(setupTask);
  const canCancel =
    isInProgress && !billingRunning && (!cancelling || cancellationStalled);
  const stepLabelColor = cancelled ? undefined : 'pml';
  const stepValueColor = cancelled ? themeColors.themeV2.subtitle : 'pml';
  const debitedAmount = billing.debited || 0;
  const totalDebitedAmount = billing.totalDebited ?? debitedAmount;
  const draftedCampaigns = billing.campaignsDrafted || 0;
  const hasCreationBilling =
    !!billing.linesValidated ||
    !!billing.linesDrafted ||
    !!billing.campaignsFailed;
  const hasUpsertBilling =
    !!upsertBilling.ranAt &&
    (!!upsertBilling.linesValidated ||
      !!upsertBilling.linesRefused ||
      !!upsertBilling.tasksUnbilled ||
      !!upsertBilling.tasksFailed);
  const billingComplete =
    !!billing.ranAt &&
    !billingRunning &&
    !billing.linesDrafted &&
    !billing.campaignsFailed &&
    !upsertBilling.linesRefused &&
    !upsertBilling.tasksFailed &&
    !upsertBilling.setupsFailed;
  const importDurationMs =
    setupTask?.createdAt && setupTask?.updatedAt && !isInProgress
      ? moment(setupTask.updatedAt).diff(moment(setupTask.createdAt))
      : null;
  const videosCount = setupTask?.counters?.creas;
  const averagesTxt =
    importDurationMs > 0 && videosCount > 0
      ? buildAveragesTxt(importDurationMs, [
          { label: 'par vidéo', count: videosCount },
          { label: 'par magasin', count: setupTask?.counters?.shops },
          { label: 'par campagne', count: setupTask?.campaigns?.length }
        ])
      : '';

  const getTaskStatusTxt = status => {
    switch (status) {
      case 'inProgress':
      case 'toDo':
        return (
          <Text component="span" fontWeight={400} color="pml">
            En cours
          </Text>
        );
      case 'done':
        return (
          <Text component="span" fontWeight={400} color="activation">
            Terminé
          </Text>
        );
      default:
        return (
          <Text component="span" fontWeight={400} color="error">
            Échec
          </Text>
        );
    }
  };

  const getCampaignStatusTxt = (campaign, task) =>
    getTaskStatusTxt(campaign?.status === 'error' ? 'error' : task.status);

  const refreshTask = async currentConfig => {
    if (!currentConfig?.id) {
      setSetupTask({});
      setTasks([]);
      return;
    }
    const [lastTask, taskList] = await Promise.all([
      PML.getLastSetupTask(currentConfig.id),
      PML.getSetupTasks(currentConfig.id, IMPORT_TYPE)
    ]);

    setSetupTask(lastTask || {});
    setTasks(taskList);
    setComputing(lastTask?.status === 'inProgress');
  };

  const getDatas = async () => {
    const [configs, domainCustomers, domainConfig] = await Promise.all([
      PML.getConfigs(IMPORT_TYPE),
      CUSTOMERS.get(),
      DOMAINCONFIG.get()
    ]);
    const currentConfig = configs[0] || { type: IMPORT_TYPE };
    const defaultCustomerId = domainConfig?.defaultStripeCustomer?.active
      ? domainConfig.defaultStripeCustomer.id
      : '';

    setCustomers(Array.isArray(domainCustomers) ? domainCustomers : []);
    setConfig({
      ...currentConfig,
      stripeCustomerId: currentConfig.stripeCustomerId || defaultCustomerId
    });
    await refreshTask(currentConfig);
    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    if (awaitingValidation || billing.status === 'inProgress') {
      billingWatched.current = true;
    }

    if (billingWatched.current && billed && quote) {
      billingWatched.current = false;
      notify({
        message: `Facturation effectuée : ${formatEuros(
          debitedAmount
        )} débités.`,
        variant: 'success'
      });
    }
  }, [
    awaitingValidation,
    billing.status,
    billed,
    quote,
    debitedAmount,
    notify
  ]);

  useEffect(() => {
    if (!awaitingValidation) {
      setConfirmAsked(false);
    }
  }, [awaitingValidation]);

  const onCancel = async () => {
    setCancelAsked(false);

    // the poll may have finished the import while the modal was open, aborting then would
    // overwrite a successful task and display it as failed
    if (!setupTask?.id || !isInProgress) {
      return;
    }

    if (awaitingValidation || cancelling) {
      if (!adminRights?.pml?.config?.update) {
        onUnauthorized();
        return;
      }

      const result = await PML.cancelBilling(setupTask.id);

      if (result?.reason === 'notAwaitingValidation') {
        notify({
          message:
            "L'import n'attend plus de validation : il ne peut plus être annulé.",
          variant: 'error'
        });
      } else if (result?.message) {
        notify({ message: BILLING_ERROR_MESSAGE, variant: 'error' });
      }
    } else {
      await PML.stopSetupTask(setupTask.id);
    }
    await refreshTask(config);
  };

  const onValidate = async () => {
    if (!adminRights?.pml?.config?.update) {
      onUnauthorized();
      return;
    }

    setValidating(true);
    const result = await PML.validateBilling(setupTask.id, quote.computedAt);

    setValidating(false);
    setConfirmAsked(false);

    if (result?.reason === 'quoteChanged') {
      notify({
        message:
          "Le montant a changé depuis l'affichage du récapitulatif : vérifiez-le avant de valider.",
        variant: 'warning'
      });
    } else if (result?.reason === 'notAwaitingValidation') {
      notify({
        message:
          "La facturation n'a pas pu être lancée : l'import n'attend plus de validation.",
        variant: 'error'
      });
    } else if (result?.message) {
      notify({ message: BILLING_ERROR_MESSAGE, variant: 'error' });
    }
    await refreshTask(config);
  };

  const onBillDrafts = async () => {
    if (!adminRights?.pml?.config?.update) {
      onUnauthorized();
      return;
    }

    await PML.retryBilling(setupTask.id);
    await refreshTask(config);
  };

  const onImport = async () => {
    if (
      !adminRights?.pml?.config?.update ||
      !adminRights?.pml?.config?.create
    ) {
      onUnauthorized();
      return;
    }

    if (!config?.googleSheetUrl || !isValidUrl(config.googleSheetUrl)) {
      setUrlError(true);
      return;
    }
    setUrlError(false);

    const payload = { ...config, type: IMPORT_TYPE };
    const savedConfig = config._id
      ? await PML.updateConfig(config._id, payload)
      : await PML.createConfig(payload);

    if (!savedConfig?.id) {
      setUrlError(true);
      return;
    }

    setConfig(savedConfig);
    setComputing(true);
    await PML.computeConfig(savedConfig.id, '');
    await refreshTask(savedConfig);
  };

  if (!loaded) {
    return (
      <Container>
        <Loading loading />
      </Container>
    );
  }

  const customerOptions = customers.map(customer => ({
    label: `${customer?.info?.name || customer?.key} (${customer?._id})`,
    value: customer._id
  }));
  // the config keeps an id : a deleted account would leave the select empty without a word
  const noBillingAccount =
    !config?.stripeCustomerId ||
    !customerOptions.some(option => option.value === config.stripeCustomerId);

  const quoteCustomer =
    customers.find(customer => customer._id === quote?.stripeCustomerId) || {};
  const customerLabel =
    quoteCustomer.info?.name ||
    quoteCustomer.key ||
    quote?.stripeCustomerId ||
    '';

  const renderConfirmModal = () => (
    <Modal
      maxWidth="xs"
      open={confirmAsked}
      onClose={() => setConfirmAsked(false)}
      actions={
        <Row
          spacing={0}
          justify="flex-start"
          style={{ gap: '12px', padding: '0 12px 12px' }}
        >
          <Item flex>
            <Button
              variant="outlined"
              color={themeColors.light}
              disabled={validating}
              style={{ border: `1px solid ${themeColors.designSystem.line}` }}
              onClick={() => setConfirmAsked(false)}
            >
              Annuler
            </Button>
          </Item>
          <Item flex>
            <Button
              variant="contained"
              color={themeColors.pml}
              disabled={validating}
              onClick={onValidate}
            >
              Confirmer
            </Button>
          </Item>
        </Row>
      }
    >
      <Container justify="flex-start" style={{ flex: 1, padding: '4px 4px 0' }}>
        <Row spacing={0} justify="flex-start">
          <Item
            flex
            justify="center"
            alignItems="center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: themeColors.designSystem.blueBg
            }}
          >
            <PaymentsOutlinedIcon
              style={{ color: themeColors.pml, transform: 'scaleX(-1)' }}
            />
          </Item>
        </Row>
        <Row spacing={0} justify="flex-start" style={{ marginTop: '16px' }}>
          <Item justify="flex-start">
            <Text
              fontSize="17px"
              fontWeight={700}
              style={{ textAlign: 'left' }}
            >
              Confirmer le débit de {formatEuros(quote.total)} sur{' '}
              {customerLabel} ?
            </Text>
          </Item>
        </Row>
        <Row spacing={0} justify="flex-start" style={{ marginTop: '10px' }}>
          <Item justify="flex-start">
            <Text
              fontSize="12.5px"
              style={{ textAlign: 'left', color: themeColors.themeV2.subtitle }}
            >
              {quote.campaignsCount} campagnes ({quote.shopsCount} magasins)
              seront facturées. Cette action est irréversible.
            </Text>
          </Item>
        </Row>
      </Container>
    </Modal>
  );

  const renderCancelModal = () => (
    <Modal
      maxWidth="xs"
      open={cancelAsked}
      onClose={() => setCancelAsked(false)}
      actions={
        <Row
          spacing={0}
          justify="flex-start"
          style={{ gap: '12px', padding: '0 12px 12px' }}
        >
          <Item flex>
            <Button
              variant="outlined"
              color={themeColors.light}
              style={{ border: `1px solid ${themeColors.designSystem.line}` }}
              onClick={() => setCancelAsked(false)}
            >
              Continuer l&apos;import
            </Button>
          </Item>
          <Item flex>
            <Button variant="contained" color="error" onClick={onCancel}>
              Arrêter
            </Button>
          </Item>
        </Row>
      }
    >
      <Container justify="flex-start" style={{ flex: 1, padding: '4px 4px 0' }}>
        <Row spacing={0} justify="flex-start">
          <Item
            flex
            justify="center"
            alignItems="center"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: themeColors.designSystem.redBg
            }}
          >
            <BlockIcon style={{ color: themeColors.error }} />
          </Item>
        </Row>
        <Row spacing={0} justify="flex-start" style={{ marginTop: '16px' }}>
          <Item justify="flex-start">
            <Text
              fontSize="17px"
              fontWeight={700}
              style={{ textAlign: 'left' }}
            >
              Annulation de l&apos;import
            </Text>
          </Item>
        </Row>
        <Row spacing={0} justify="flex-start" style={{ marginTop: '10px' }}>
          <Item justify="flex-start">
            <Text
              fontSize="12.5px"
              style={{ textAlign: 'left', color: themeColors.themeV2.subtitle }}
            >
              {awaitingValidation || cancelling
                ? "L'import sera annulé : aucun montant ne sera débité et les campagnes créées par cet import seront supprimées."
                : "L'import en cours sera arrêté. Les créas déjà générées et les campagnes déjà créées sont conservées, le reste ne sera pas repris."}
            </Text>
          </Item>
        </Row>
      </Container>
    </Modal>
  );

  return (
    <Container>
      <IntervalTimer
        timeout={3000}
        callback={() => refreshTask(config)}
        enabled={!!config?.id}
        repeat
      />
      {cancelAsked && renderCancelModal()}
      {confirmAsked && awaitingValidation && renderConfirmModal()}
      {renderImportTypeSelect()}
      <Row>
        <Item>
          <Text fontWeight={700} fontSize="14px">
            Import campagnes &amp; créas repiquées
          </Text>
        </Item>
      </Row>
      <Row>
        <Item xs={8}>
          <Text fontSize="12px">
            Collez le lien du Google Sheet enrichi. Le fichier campagnes est
            complété en amont, hors application : vous n’avez qu’à fournir le
            lien pour lancer l’import.
          </Text>
        </Item>
      </Row>
      <Row spacing={4}>
        <Item xs={8}>
          <TextField
            title="Lien du Google Sheet"
            placeholder="https://docs.google.com/spreadsheets/d/…"
            value={config?.googleSheetUrl || ''}
            error={urlError}
            errorText="Lien Google Sheet invalide ou inaccessible."
            onChange={value => {
              setUrlError(false);
              setConfig({ ...config, googleSheetUrl: value });
            }}
          />
        </Item>
        <Item xs={4} style={{ marginTop: '2px' }}>
          <Select
            title="Compte de facturation"
            options={customerOptions}
            value={config?.stripeCustomerId || ''}
            onChange={value =>
              setConfig({ ...config, stripeCustomerId: value })
            }
            small={false}
            width="100%"
            sx={{ height: '33px' }}
          />
        </Item>
      </Row>
      {noBillingAccount && (
        <Row>
          <Item xs={8}>
            <Text fontSize="12px" color="error">
              Sans compte de facturation valide, les campagnes seront importées
              en brouillon.
            </Text>
          </Item>
        </Row>
      )}
      <Row>
        <Item flex style={{ padding: '10px' }}>
          <Button
            onClick={onImport}
            variant="contained"
            disabled={computing}
            upperCase
          >
            <Container spacing={0}>
              <Row spacing={0} alignItems="center">
                <Item flex style={{ paddingRight: '10px' }}>
                  {computing ? (
                    <Loading loading width="24px" color={themeColors.black} />
                  ) : (
                    <FlightTakeoffIcon />
                  )}
                </Item>
                <Item xs>
                  <Text fontSize="inherit" fontWeight="inherit" color="inherit">
                    {setupTask?.id
                      ? 'Réimporter les créas'
                      : 'Importer les créas'}
                  </Text>
                </Item>
              </Row>
            </Container>
          </Button>
        </Item>
        {canCancel && (
          <Item flex style={{ padding: '10px' }}>
            <Button
              onClick={() => setCancelAsked(true)}
              variant="outlined"
              color={
                awaitingValidation ? themeColors.themeV2.baseGrey : 'error'
              }
              disabled={awaitingValidation}
              upperCase
            >
              <Text fontSize="inherit" fontWeight="inherit" color="inherit">
                Annuler l&apos;import
              </Text>
            </Button>
          </Item>
        )}
        {!isInProgress && !billingRunning && draftedCampaigns > 0 && (
          <Item flex style={{ padding: '10px' }}>
            <Button
              onClick={onBillDrafts}
              variant="outlined"
              color={themeColors.pml}
              upperCase
            >
              <Text fontSize="inherit" fontWeight="inherit" color="inherit">
                Facturer les campagnes en brouillon
              </Text>
            </Button>
          </Item>
        )}
      </Row>
      {setupTask?.status === 'done' && (
        <Row>
          <Item xs={8}>
            <Text fontSize="12px" color="activation">
              {(upsertBilling.linesValidated || upsertBilling.tasksUnbilled) &&
              !hasCreationBilling
                ? 'Import terminé : campagnes existantes mises à jour.'
                : 'Import terminé : créas générées, assets enregistrés et campagnes créées.'}
            </Text>
          </Item>
        </Row>
      )}
      {setupTask?.counters?.read > 0 && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px">
              {setupTask.counters.read} lignes lues ·{' '}
              {setupTask.counters.imported} importées ·{' '}
              {setupTask.counters.error} en erreur
            </Text>
          </Item>
        </Row>
      )}
      {billing.ranAt && hasCreationBilling && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px">
              Facturation : {billing.linesValidated} campagnes débitées et
              setupées ({billing.campaignsValidated} déclinaisons magasin) ·{' '}
              {billing.linesDrafted} en brouillon ({draftedCampaigns}{' '}
              déclinaisons) · {billing.debited} € débités
            </Text>
          </Item>
        </Row>
      )}
      {hasUpsertBilling && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px">
              Upserts : {upsertBilling.linesValidated} campagnes mises à jour et
              débitées ({upsertBilling.tasksValidated} déclinaisons magasin) ·{' '}
              {upsertBilling.linesRefused} non appliquées faute de solde (
              {upsertBilling.tasksRefused} déclinaisons) ·{' '}
              {upsertBilling.debited} € débités
              {upsertBilling.tasksUnbilled
                ? ` · ${upsertBilling.tasksUnbilled} déclinaisons appliquées sans supplément`
                : ''}
            </Text>
          </Item>
        </Row>
      )}
      {billing.ranAt && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px">
              Solde restant :{' '}
              {upsertBilling.ranAt
                ? upsertBilling.balanceAfter
                : billing.balanceAfter}{' '}
              €
            </Text>
          </Item>
        </Row>
      )}
      {billingComplete && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px" color="activation">
              Toutes les campagnes de cet import sont facturées et validées.
            </Text>
          </Item>
        </Row>
      )}
      {billing.campaignsFailed > 0 && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px" color="error">
              {billing.campaignsFailed} campagnes n’ont pas pu être débitées,
              voir les motifs ci-dessous.
            </Text>
          </Item>
        </Row>
      )}
      {upsertBilling.tasksFailed > 0 && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px" color="error">
              {upsertBilling.tasksFailed} upserts n’ont pas pu être débités,
              voir les motifs ci-dessous.
            </Text>
          </Item>
        </Row>
      )}
      {upsertBilling.status === 'error' && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px" color="error">
              La facturation des upserts a été interrompue : aucun supplément
              n’a été appliqué, relancer l’import.
            </Text>
          </Item>
        </Row>
      )}
      {upsertBilling.setupsFailed > 0 && (
        <Row>
          <Item xs={8} justify="flex-start">
            <Text fontSize="12px" color="error">
              {upsertBilling.setupsFailed} upserts ont été débités sans que leur
              setup démarre : les relancer depuis la campagne.
            </Text>
          </Item>
        </Row>
      )}
      {(isInProgress || billingRunning || cancelled) && (
        <>
          <Row>
            <Item xs={8} justify="flex-start">
              <Text fontSize="12px" fontWeight={700} color={stepLabelColor}>
                Étape :&nbsp;
                <Text
                  component="span"
                  fontSize="12px"
                  fontWeight={400}
                  color={stepValueColor}
                >
                  {setupTask.step || 'Lancement du setup'}
                </Text>
              </Text>
            </Item>
          </Row>
          <Row>
            <Item xs={8}>
              <LinearProgress
                color={themeColors.pml}
                value={setupTask.progress || 0}
                variant="determinate"
                showValue
              />
            </Item>
          </Row>
        </>
      )}
      {cancelled && (
        <Row>
          <Item xs={8}>
            <Row
              spacing={0}
              justify="flex-start"
              alignItems="center"
              style={{
                gap: '12px',
                padding: '14px 20px',
                marginTop: '8px',
                borderRadius: '8px',
                border: `1px solid ${themeColors.designSystem.line}`,
                backgroundColor: themeColors.designSystem.bgSoft
              }}
            >
              <Item flex>
                <BlockIcon
                  fontSize="small"
                  style={{ color: themeColors.light }}
                />
              </Item>
              <Item flex>
                <Text fontSize="13px">{setupTask.message}</Text>
              </Item>
            </Row>
          </Item>
        </Row>
      )}
      {showQuote && (
        <Row>
          <Item xs={12}>
            <BillingQuote
              quote={quote}
              campaigns={setupTask.campaigns || []}
              customerName={customerLabel}
              awaitingValidation={awaitingValidation}
              billed={billed}
              debitedAmount={totalDebitedAmount}
              busy={validating}
              onValidate={() => setConfirmAsked(true)}
              onCancel={() => setCancelAsked(true)}
            />
          </Item>
        </Row>
      )}
      {setupTask?.status === 'error' && (
        <>
          <Row>
            <Item xs={8}>
              <Text
                fontSize="12px"
                color="error"
                style={{ overflowWrap: 'anywhere' }}
              >
                {setupTask.message}
              </Text>
            </Item>
          </Row>
          {!setupTask.retryLater && (
            <Row>
              <Item xs={8}>
                <Text fontSize="12px">
                  Corrigez les créas en erreur directement dans le Google Sheet,
                  puis relancez l’import.
                </Text>
              </Item>
            </Row>
          )}
        </>
      )}
      {setupTask?.report?.length > 0 && (
        <Row>
          <Item>
            <Table
              numberPerRow={10}
              headers={[
                { id: 'tab', label: 'Onglet' },
                { id: 'line', label: 'Ligne' },
                { id: 'column', label: 'Colonne' },
                { id: 'reason', label: 'Motif' }
              ]}
              rows={setupTask.report.map((el, index) => ({
                key: `${el.tab}-${el.line}-${el.column}-${index}`,
                tab: el.tab,
                line: el.line,
                column: el.column,
                reason: el.reason
              }))}
            />
          </Item>
        </Row>
      )}
      {setupTask?.warnings?.length > 0 && (
        <Row>
          <Accordion
            onExpandChange={() => setWarningsExpanded(!warningsExpanded)}
            expanded={warningsExpanded}
            iconColor={themeColors.pml}
            rows={[
              {
                key: 'warnings',
                label: (
                  <Row alignItems="center" justify="flex-start" spacing={0}>
                    <Item xs justify="flex-start" alignItems="center">
                      <WarningRoundedIcon />
                      <Text
                        noWrap
                        fontWeight={700}
                        style={{ paddingLeft: '7px' }}
                      >
                        Avertissements ({setupTask.warnings.length})
                      </Text>
                    </Item>
                  </Row>
                ),
                items: setupTask.warnings.map(el => <Text>{el}</Text>)
              }
            ]}
            underline
          />
        </Row>
      )}
      {setupTask?.counters?.creas > 0 && (
        <Row>
          <Item xs={12} justify="flex-start" alignItems="center">
            <ImageOutlinedIcon />
            <Text noWrap fontWeight={700} style={{ paddingLeft: '7px' }}>
              Aperçu des créas, avant repiquage de l&apos;adresse
            </Text>
          </Item>
          <Item xs={12}>
            <PreviewGallery
              key={setupTask.id}
              previews={setupTask?.previews || []}
              accentColor={themeColors.pml}
            />
          </Item>
        </Row>
      )}
      <Row>
        <Item>
          <Text fontWeight={700} fontSize="14px">
            Historique de vos repiquages
          </Text>
        </Item>
      </Row>
      {setupTask?.id && (
        <Row>
          <Item xs={12} justify="flex-start">
            <Text fontWeight={700}>
              Date du dernier import :&nbsp;
              <Text component="span" fontWeight={400}>
                {moment(
                  isInProgress ? setupTask.createdAt : setupTask.updatedAt
                ).format('DD/MM/YYYY HH:mm:ss')}
              </Text>
            </Text>
          </Item>
          <Item xs={12} justify="flex-start">
            <Text fontWeight={700}>
              Status :&nbsp;
              {getTaskStatusTxt(setupTask.status)}
            </Text>
          </Item>
          {importDurationMs > 0 && (
            <Item xs={12} justify="flex-start">
              <Text fontWeight={700}>
                Durée de l&apos;import :&nbsp;
                <Text component="span" fontWeight={400}>
                  {formatDuration(importDurationMs)}
                </Text>
              </Text>
            </Item>
          )}
          {averagesTxt && (
            <Item xs={12} justify="flex-start">
              <Text fontWeight={700}>
                Coût moyen à l&apos;unité :&nbsp;
                <Text component="span" fontWeight={400}>
                  {averagesTxt}
                </Text>
              </Text>
            </Item>
          )}
        </Row>
      )}
      <Row>
        <Item>
          <Table
            numberPerRow={10}
            headers={[
              { id: 'campaignKey', label: 'Campagne source' },
              { id: 'shops', label: 'Magasins repiqués' },
              { id: 'creatives', label: 'Créas repiquées' },
              { id: 'status', label: 'Status' },
              { id: 'reason', label: 'Motif' }
            ]}
            rows={lastRepiquageByCampaign(tasks).map(({ campaign, task }) => ({
              id: campaignKeyOf(campaign) || task.id,
              campaignKey: campaign.key || '-',
              shops: campaign.shopsTargeted ?? '-',
              creatives: campaign.creativesCount ?? '-',
              status: getCampaignStatusTxt(campaign, task),
              reason: campaign.reason || '-'
            }))}
          />
        </Item>
      </Row>
    </Container>
  );
};

RepiquageImport.propTypes = {
  renderImportTypeSelect: PropTypes.func.isRequired,
  onUnauthorized: PropTypes.func.isRequired
};

export default RepiquageImport;
